import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import api from "../lib/api";
import { formatPrice, typeLabel } from "../lib/format";
import { ScoreBar } from "../components/Score";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function LocationDetail() {
  const { slug } = useParams();
  const { data: l, isLoading } = useQuery({ queryKey: ["location", slug], queryFn: async () => (await api.get(`/locations/${slug}`)).data });
  if (isLoading) return <div className="max-w-7xl mx-auto px-4 py-16"><div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /></div>;
  if (!l) return <div className="py-24 text-center text-slate-500">Location not found.</div>;

  const scores = [["Connectivity", l.connectivity], ["Schools", l.schools], ["Hospitals", l.hospitals], ["Market", l.market], ["Investment", l.investment]].filter(([, v]) => v);

  return (
    <div>
      <Seo title={l.seo_title || `Property for Sale in ${l.name} Faridabad`} description={l.seo_description || `${l.property_count} verified properties in ${l.name}, Faridabad. ${l.description}`} />
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/30" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-white">
          <div className="flex items-center gap-1.5 text-sm text-white/80"><MapPin className="w-4 h-4" /> Faridabad, Haryana</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold mt-1">{l.name}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            <span><b className="font-heading">{l.property_count}</b> Properties</span>
            {l.price_min && <span>Starting from <b className="font-heading">{formatPrice(l.price_min)}</b></span>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[1fr_320px] gap-10">
        <div>
          <p className="text-slate-600 leading-relaxed">{l.description}</p>
          {l.property_types?.length ? (
            <div className="flex flex-wrap gap-2 mt-4">{l.property_types.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full">{typeLabel(t)}</span>)}</div>
          ) : null}

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-10 mb-5">Properties in {l.name}</h2>
          {l.properties?.length ? (
            <div className="grid sm:grid-cols-2 gap-5">{l.properties.map((p) => <PropertyCard key={p.id} p={p} />)}</div>
          ) : (
            <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-slate-500">No active listings in this sector right now. <Link to="/properties" className="text-indigo-800 font-semibold">Browse all →</Link></div>
          )}

          {l.faqs?.length ? (
            <div className="mt-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">FAQs about {l.name}</h2>
              <Accordion type="single" collapsible>{l.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left">{f.question}</AccordionTrigger><AccordionContent>{f.answer}</AccordionContent></AccordionItem>
              ))}</Accordion>
            </div>
          ) : null}
        </div>

        <aside>
          <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Locality Overview</h3>
            <div className="space-y-4">{scores.map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
